import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import api from "@/lib/api";
import { Sparkles, Award, TrendingUp, Compass, GraduationCap, Route, Loader2, Printer, BarChart3, ArrowRight as ArrowRightIcon, RefreshCw, Landmark, MapPin, Timer, Smile } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Cell } from "recharts";
import CohortComparison from "@/components/CohortComparison";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { localizeCareer } from "@/lib/careerNames";
import { CATEGORY_META } from "@/lib/categoryMeta";
import { toast } from "sonner";

const ENGAGEMENT_BANDS = [
  { min: 80, label: "Focused", color: "bg-[#A7F3D0]" },
  { min: 55, label: "Deliberate", color: "bg-[#FEF08A]" },
  { min: 0, label: "Rushed", color: "bg-[#FFDDBF]" },
];
const engagementBand = (score) => ENGAGEMENT_BANDS.find((b) => score >= b.min) || ENGAGEMENT_BANDS[ENGAGEMENT_BANDS.length - 1];

const EXPRESSION_META = {
  happy: { label: "Happy / Engaged", color: "bg-[#A7F3D0]" },
  surprised: { label: "Surprised / Curious", color: "bg-[#FEF08A]" },
  neutral: { label: "Neutral / Focused", color: "bg-white" },
  sad: { label: "Low energy", color: "bg-[#FFDDBF]" },
  angry: { label: "Frustrated", color: "bg-[#FFDDBF]" },
  fearful: { label: "Anxious", color: "bg-[#FFDDBF]" },
  disgusted: { label: "Uneasy", color: "bg-[#FFDDBF]" },
};
const expressionMeta = (label) => EXPRESSION_META[label] || { label: label || "—", color: "bg-white" };

const AI_IMG =
  "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&w=1000&q=85";

const ALIGNMENT_STYLE = {
  strong: { color: "bg-[#A7F3D0]", label: "Strong alignment" },
  moderate: { color: "bg-[#FEF08A]", label: "Moderate alignment" },
  needs_reflection: { color: "bg-[#FFDDBF]", label: "Needs reflection" },
};

export default function Report() {
  const { id } = useParams();
  const { lang } = useLang();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    api.get(`/results/${id}`).then((r) => setData(r.data));
  }, [id]);

  const regenerate = async () => {
    if (regenerating) return;
    setRegenerating(true);
    try {
      const { data: fresh } = await api.post(`/results/${id}/regenerate`, { language: lang });
      setData(fresh);
      toast.success("Report regenerated in your language");
    } catch (e) {
      toast.error("Regeneration failed — please try again");
    } finally { setRegenerating(false); }
  };

  if (!data) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-20 flex items-center gap-3">
          <Loader2 className="animate-spin" /> <span className="label-mono">Loading report…</span>
        </div>
      </div>
    );
  }

  const r = data.ai_report || {};
  const align = ALIGNMENT_STYLE[r.path_alignment] || ALIGNMENT_STYLE.moderate;

  const traitData = Object.entries(data.scores?.trait_scores || {}).map(([trait, value]) => ({ trait, value }));

  const behaviorMetrics = data.scores?.behavior_metrics;
  const questionTimeData = (data.scores?.answer_log || [])
    .filter((e) => e.time_spent_seconds != null)
    .map((e, idx) => ({
      name: `Q${idx + 1}`,
      seconds: e.time_spent_seconds,
      category: e.category,
    }));
  const band = behaviorMetrics ? engagementBand(behaviorMetrics.engagement_index) : null;

  const expressionMetrics = data.scores?.expression_metrics;
  const expressionRows = (data.scores?.answer_log || [])
    .map((e, idx) => ({ ...e, num: idx + 1 }))
    .filter((e) => e.expression);
  const dominantMeta = expressionMetrics ? expressionMeta(expressionMetrics.dominant_expression) : null;

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        {/* Report tagline banner */}
        <div data-testid="report-tagline-banner" className="mb-6 card-brutal p-4 flex items-center gap-3 bg-[#E9D5FF]">
          <Sparkles size={20} strokeWidth={2.5} className="shrink-0" />
          <div>
            <div className="label-mono">AI Career Consulting Report</div>
            <div className="text-sm text-[#0A0A0A]/80 italic">"Grounded in your answers, not guesswork — your path, mapped by AI."</div>
          </div>
        </div>
        {/* Header */}
        <div className="print:hidden mb-4 flex flex-wrap justify-end gap-2">
          {user?.role === "student" && data.user_id === user.id && (
            <button
              onClick={regenerate}
              disabled={regenerating}
              data-testid="regenerate-btn"
              className="btn-brutal bg-[#A7F3D0] px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60"
            >
              {regenerating ? <Loader2 className="animate-spin" size={14} /> : <RefreshCw size={14} strokeWidth={2.5} />}
              Regenerate in {lang === "hi" ? "हिंदी" : lang === "pa" ? "ਪੰਜਾਬੀ" : "English"}
            </button>
          )}
          <Link to={`/certificate/${id}`} data-testid="view-certificate-btn" className="btn-brutal bg-[#FEF08A] px-4 py-2 text-sm flex items-center gap-2">
            <Award size={16} strokeWidth={2.5} /> View Certificate
          </Link>
          <button data-testid="print-report-btn" onClick={() => window.print()} className="btn-brutal bg-[#0A0A0A] text-white px-4 py-2 text-sm flex items-center gap-2">
            <Printer size={16} strokeWidth={2.5} /> Save / Print PDF
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-8">
          <div className="md:col-span-8 card-brutal p-8">
            <span className="label-mono flex items-center gap-2"><Sparkles size={14} strokeWidth={2.5} /> AI Career Report</span>
            <h1 data-testid="report-title" className="font-display text-3xl md:text-5xl font-extrabold tracking-tighter mt-3">
              Hey {data.user_name}, here's your path.
            </h1>
            <p className="mt-4 text-base md:text-lg leading-relaxed text-[#0A0A0A]">{r.summary}</p>
            <div className={`mt-6 inline-flex items-center gap-2 px-3 py-1 border-2 border-[#0A0A0A] rounded-full ${align.color}`}>
              <span className="label-mono">{align.label}</span>
            </div>
          </div>
          <div className="md:col-span-4 card-brutal p-4 bg-[#E9D5FF]">
            <img src={AI_IMG} alt="AI matrix visualisation" className="w-full h-56 object-cover rounded-xl border-2 border-[#0A0A0A]" onError={(e) => { e.currentTarget.src = "https://images.pexels.com/photos/60504/security-protection-anti-virus-software-60504.jpeg?auto=compress&cs=tinysrgb&w=800"; }} />
            <p className="mt-3 text-sm font-medium">AI-generated career report</p>
          </div>
        </div>

        {/* Top Careers */}
        <section className="mb-10">
          <span className="label-mono">Top matches</span>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-1 mb-6">Careers that fit you</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(r.top_careers || []).map((c, i) => (
              <div key={i} data-testid={`career-card-${i}`} className="card-brutal p-6">
                <div className="flex items-start justify-between">
                  <Award strokeWidth={2.5} size={26} />
                  <span className="label-mono bg-[#A7F3D0] px-2 py-1 border-2 border-[#0A0A0A] rounded-full">{c.match_percent}%</span>
                </div>
                <h3 className="font-display text-xl font-bold mt-3">{localizeCareer(c.title, lang)}</h3>
                <p className="text-sm mt-2 text-[#52525B] leading-relaxed">{c.why}</p>
                {c.typical_subjects?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {c.typical_subjects.map((s, k) => (
                      <span key={k} className="text-xs px-2 py-1 bg-[#FEF08A] border-2 border-[#0A0A0A] rounded-full">{s}</span>
                    ))}
                  </div>
                )}
                <Link
                  to={`/career/${encodeURIComponent(c.title)}`}
                  data-testid={`explore-career-${i}`}
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold underline underline-offset-4 print:hidden"
                >
                  Explore this career <ArrowRightIcon size={14} strokeWidth={2.5} />
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Personality + Chart */}
        <section className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-10">
          <div className="md:col-span-7 card-brutal p-8">
            <span className="label-mono flex items-center gap-2"><Compass size={14} strokeWidth={2.5} /> Personality</span>
            <h2 className="font-display text-2xl font-extrabold tracking-tight mt-2">Who you are</h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed">{r.personality_analysis}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
              <div className="bg-[#A7F3D0] border-2 border-[#0A0A0A] rounded-xl p-4">
                <div className="label-mono flex items-center gap-1"><TrendingUp size={12} strokeWidth={2.5} /> Strengths</div>
                <ul className="mt-2 space-y-1 text-sm">
                  {(r.strengths || []).map((s, i) => (
                    <li key={i}>• {s}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#FFDDBF] border-2 border-[#0A0A0A] rounded-xl p-4">
                <div className="label-mono">Growth areas</div>
                <ul className="mt-2 space-y-1 text-sm">
                  {(r.growth_areas || []).map((s, i) => (
                    <li key={i}>• {s}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="md:col-span-5 card-brutal p-6">
            <span className="label-mono">Your trait signature</span>
            <div className="h-72 mt-4">
              {traitData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={traitData}>
                    <PolarGrid stroke="#0A0A0A" />
                    <PolarAngleAxis dataKey="trait" tick={{ fontSize: 11, fill: "#0A0A0A" }} />
                    <PolarRadiusAxis tick={false} />
                    <Radar dataKey="value" stroke="#2563EB" fill="#3B82F6" fillOpacity={0.4} />
                  </RadarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-[#52525B]">No trait data</div>
              )}
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
              <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-2">
                <div className="label-mono">Aptitude</div>
                <div className="font-display font-extrabold">{data.scores?.aptitude?.correct}/{data.scores?.aptitude?.total}</div>
              </div>
              <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-2">
                <div className="label-mono">Mental Ability</div>
                <div className="font-display font-extrabold">{data.scores?.mental_ability?.correct}/{data.scores?.mental_ability?.total}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Behavioral insights */}
        {behaviorMetrics && (
          <section data-testid="behavior-section" className="mb-10">
            <span className="label-mono flex items-center gap-2"><Timer size={14} strokeWidth={2.5} /> Behavior &amp; focus</span>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-1 mb-6">How you took this assessment</h2>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <div className="md:col-span-7 card-brutal p-8">
                <p className="text-sm md:text-base leading-relaxed">{r.behavioral_insights}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                  <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-3">
                    <div className="label-mono">Total time</div>
                    <div className="font-display font-extrabold">
                      {Math.floor(behaviorMetrics.total_time_seconds / 60)}m {Math.round(behaviorMetrics.total_time_seconds % 60)}s
                    </div>
                  </div>
                  <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-3">
                    <div className="label-mono">Avg / question</div>
                    <div className="font-display font-extrabold">{behaviorMetrics.avg_time_per_question}s</div>
                  </div>
                  <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-3">
                    <div className="label-mono">Answer changes</div>
                    <div className="font-display font-extrabold">{behaviorMetrics.total_answer_changes}</div>
                  </div>
                  <div data-testid="engagement-tile" className={`border-2 border-[#0A0A0A] rounded-lg p-3 ${band?.color || "bg-white"}`}>
                    <div className="label-mono">Engagement</div>
                    <div className="font-display font-extrabold">{band?.label}</div>
                  </div>
                </div>
                <p className="text-xs text-[#52525B] mt-3">
                  Engagement is an indicative signal based on pacing and answer changes — not a diagnostic score.
                </p>
              </div>

              <div className="md:col-span-5 card-brutal p-6">
                <span className="label-mono">Time per question</span>
                <div className="h-64 mt-4">
                  {questionTimeData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={questionTimeData}>
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                        <Bar dataKey="seconds">
                          {questionTimeData.map((d, idx) => (
                            <Cell key={idx} fill={CATEGORY_META[d.category]?.hex || "#3B82F6"} stroke="#0A0A0A" strokeWidth={2} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-sm text-[#52525B]">No timing data</div>
                  )}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Expression insights */}
        {expressionMetrics && (
          <section data-testid="expression-section" className="mb-10">
            <span className="label-mono flex items-center gap-2"><Smile size={14} strokeWidth={2.5} /> Expression insights</span>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-1 mb-6">How you seemed while answering</h2>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
              <div className="md:col-span-7 card-brutal p-8">
                <p className="text-sm md:text-base leading-relaxed">{r.expression_insights}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
                  <div data-testid="dominant-expression-tile" className={`border-2 border-[#0A0A0A] rounded-lg p-3 ${dominantMeta?.color || "bg-white"}`}>
                    <div className="label-mono">Dominant mood</div>
                    <div className="font-display font-extrabold">{dominantMeta?.label}</div>
                  </div>
                  <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-3">
                    <div className="label-mono">Positive / neutral</div>
                    <div className="font-display font-extrabold">{expressionMetrics.positive_ratio}%</div>
                  </div>
                  <div className="bg-white border-2 border-[#0A0A0A] rounded-lg p-3">
                    <div className="label-mono">Samples captured</div>
                    <div className="font-display font-extrabold">{expressionMetrics.sample_count}</div>
                  </div>
                </div>
                <p className="text-xs text-[#52525B] mt-3">
                  Captured from an optional on-device camera check — a light-touch, indicative signal, not a diagnosis. No video was ever uploaded.
                </p>
              </div>

              <div className="md:col-span-5 card-brutal p-6 overflow-x-auto">
                <span className="label-mono">Per-question expression</span>
                <table className="w-full mt-4 text-sm border-collapse">
                  <thead>
                    <tr className="text-left border-b-2 border-[#0A0A0A]">
                      <th className="py-2 pr-2 label-mono">Q#</th>
                      <th className="py-2 pr-2 label-mono">Category</th>
                      <th className="py-2 pr-2 label-mono">Time</th>
                      <th className="py-2 pr-2 label-mono">Expression</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expressionRows.map((row) => {
                      const meta = expressionMeta(row.expression);
                      return (
                        <tr key={row.question_id} data-testid={`expression-row-${row.num}`} className="border-b border-[#0A0A0A]/10">
                          <td className="py-2 pr-2">Q{row.num}</td>
                          <td className="py-2 pr-2 capitalize">{row.category}</td>
                          <td className="py-2 pr-2">{row.time_spent_seconds != null ? `${row.time_spent_seconds}s` : "—"}</td>
                          <td className="py-2 pr-2">
                            <span className={`px-2 py-0.5 border-2 border-[#0A0A0A] rounded-full text-xs ${meta.color}`}>{meta.label}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        )}

        {/* Stream + NEP + Board */}
        <section className="mb-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 card-brutal p-8 bg-[#FEF08A]">
            <span className="label-mono flex items-center gap-2"><GraduationCap size={14} strokeWidth={2.5} /> Recommended stream</span>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-3">{r.recommended_stream}</h2>
            {r.nep_alignment && (
              <div className="mt-5 bg-white border-2 border-[#0A0A0A] rounded-xl p-4">
                <span className="label-mono">NEP 2020 alignment</span>
                <p className="text-sm mt-2 leading-relaxed">{r.nep_alignment}</p>
              </div>
            )}
          </div>
          <div className="card-brutal p-6 bg-[#A7F3D0]">
            <span className="label-mono">Board note · {data.education_board || "N/A"}</span>
            <p className="text-sm mt-3 leading-relaxed">{r.board_notes || "Follow your board's flexible skill electives."}</p>
            {data.nep_stage && (
              <div className="mt-4 text-xs">
                <div className="font-bold">NEP Stage</div>
                <div>{data.nep_stage.stage}</div>
                <div className="text-[#52525B] mt-1">{data.nep_stage.focus}</div>
              </div>
            )}
          </div>
        </section>

        {/* Roadmap */}
        <section className="mb-14">
          <span className="label-mono flex items-center gap-2"><Route size={14} strokeWidth={2.5} /> Your roadmap</span>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-1 mb-6">What to do next</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(r.roadmap || []).map((s, i) => (
              <div key={i} className="card-brutal p-6">
                <span className="font-display text-4xl font-extrabold text-blue-600">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="font-display text-lg font-bold mt-2">{s.stage}</h3>
                <ul className="mt-3 space-y-2 text-sm">
                  {(s.actions || []).map((a, k) => (
                    <li key={k}>• {a}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Top Colleges */}
        {(r.top_colleges || []).length > 0 && (
          <section className="mb-14">
            <span className="label-mono flex items-center gap-2"><Landmark size={14} strokeWidth={2.5} /> Where to aim</span>
            <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-1 mb-6">Top colleges for your path</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {r.top_colleges.map((c, i) => (
                <div key={i} data-testid={`college-card-${i}`} className="card-brutal p-6 bg-[#E9D5FF]">
                  <Landmark strokeWidth={2.5} size={24} />
                  <h3 className="font-display text-lg font-bold mt-3">{c.name}</h3>
                  {c.location && (
                    <div className="flex items-center gap-1 text-xs text-[#52525B] mt-1">
                      <MapPin size={12} strokeWidth={2.5} /> {c.location}
                    </div>
                  )}
                  {c.why && <p className="text-sm mt-3 text-[#0A0A0A]/80 leading-relaxed">{c.why}</p>}
                  {c.how_to_get_in && (
                    <div className="mt-4 bg-white border-2 border-[#0A0A0A] rounded-xl p-3">
                      <span className="label-mono">How to get in</span>
                      <p className="text-sm mt-2 leading-relaxed">{c.how_to_get_in}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Cohort Comparison */}
        <section className="mb-14">
          <CohortComparison resultId={id} />
        </section>

        {r.encouragement && (
          <div className="card-brutal p-6 bg-[#A7F3D0] text-center">
            <p className="font-display text-lg font-bold">{r.encouragement}</p>
          </div>
        )}

        <div className="mt-10 flex justify-center">
          <Link to="/dashboard" data-testid="back-dashboard" className="btn-brutal bg-white px-6 py-3">Back to dashboard</Link>
        </div>
      </div>
    </div>
  );
}
